/**
 * Created by Thomas Mark
 * Date: 2020-05-01
 * 
 * Search Dialog
 * 
 * Hier kann der Benutzer nach einem Produkt suchen
 * 
 * In diesem Dialog hat der Benutzer die Möglichkeit einen Suchbegriff einzugeben. 
 * Die Suche startet nach Eingabe des ersten Zeichens. Die Debounce-Mechanik verhindert
 * das direkt nach Eingabe eines oder mehrerer Zeichen in kurzer Folge, sofort gesucht 
 * wird. 
 * 
 * K-Log Erweiterung - Bilder Suchen
 * 
 * Im S-K-Log Katalog können den Produkten auch Bilder zugeordnet werden. Falls einem
 * Produkt Bilder zugeordnet wurden, so werden diese unterhalb der Produktzeile angezeigt. 
 *   
 * Ist der S-K-Log Katalog "Regal- und Lagersysteme" aktiv, so wird oben rechts der
 * Schalter 'nur Bilder' angezeigt, der die Variable 'searchImagesOnly' kontrolliert.
 * Ist dieser Schalter gesetzt, so werden nur noch die Bilder aller Produkte angezeigt, 
 * die Produktzeile verschwindet komplett.
 * 
 * Wird ein Bild angeklickt, so wird der ImageGalleryModal-Dialog geöffnet und das ausgewählte 
 * Bild wird innerhalb der ImageGallery angezeigt. 
 * 
 * 
 */
import { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { IconPortfolio } from "../../partials/Icons";
import { DebounceInput } from 'react-debounce-input';

import { Icon } from '../../partials/Icons';
import PDFFrame from "../../partials/PDFFrame";
import ProductDetails from "../product/ProductDetails";
import * as utils from "../../../basics/utils";
import * as cordova from "../../../basics/cordova";
import * as appActions from "../../../store/actions/appActions";
import * as portfolioActions from "../../../store/actions/portfolioActions";
import { ImageLoader } from "../../partials/ImageLoader";
import Switch from "@material-ui/core/Switch"

const SearchController = () => {
    const dispatch = useDispatch();
    const app = useSelector(state => state.app);

    const { searchImagesOnly, inputHeight, unitHeight, headerHeight, toolbarType, productList, filterList, searchString, orientation, currentContainer, appModus } = app;

    // Action-Dispatcher
    const setSearchString = useCallback(str => dispatch(appActions.setSearchString(str)), [dispatch]);
    const setGallerySearch = useCallback(flag => dispatch(appActions.setGallerySearch(flag)), [dispatch]);
    const setFilterList = useCallback(list => dispatch(appActions.setFilterList(list)), [dispatch]);
    const setGalleryImages = useCallback(images => dispatch(appActions.setGalleryImages(images)), [dispatch]);
    const setSearchIndex = useCallback((idx, path) => dispatch(appActions.setSearchIndex(idx, path)), [dispatch]);
    const setCurrentProduct = useCallback(prod => dispatch(portfolioActions.setCurrentProduct(prod)), [dispatch]);
    const setCurrentPortfolio = useCallback(pf => dispatch(portfolioActions.setCurrentPortfolio(pf)), [dispatch]);
    const setGalleryIndex = useCallback(idx => dispatch(appActions.setGalleryIndex(idx)), [dispatch]);
    const setModalDialog = useCallback((type, data) => dispatch(appActions.setModalDialog(type, data)), [dispatch]);
    const setSearchImagesOnly = useCallback(val => dispatch(appActions.setSearchImagesOnly(val)), [dispatch]);

    const debounceTimeout = 400; // Anzahl Millisekunden in denen eine Eigabe gepuffert wird.
    const minLength = 1; // 

    const itemHeight = orientation === 'landscape' ? inputHeight / 4 : inputHeight / 6;

    const [products, setProducts] = useState([]);
    const [images, setImages] = useState([]);

    // ==========================================================================
    //
    // HELPER - SECTION
    //
    // ==========================================================================
    const searchImageList = (searchString) => {
        if (!searchString?.trim()) return [];

        const searchUpper = searchString.replace(/\s/g, '').toUpperCase();

        return productList.flatMap(product => {
            const images = product.images || [];
            return images
                .filter(image =>
                    (image.tags || []).some(tag =>
                        tag.toUpperCase().includes(searchUpper)
                    )
                )
                .map(image => ({
                    path: product.path,
                    file: image.file,
                    tags: image.tags
                }));
        });
    };

    const searchProductList = (searchString) => {

        const normalizedSearch = searchString.replace(/\s/g, '').toUpperCase();

        if (!normalizedSearch && filterList.length === 0) return [];

        const matchesSearch = (text) =>
            text.replace(/\s/g, '').toUpperCase().includes(normalizedSearch);

        const buildProductString = (product) => {
            const { name, meta, images } = product;
            const parts = [
                name,
                meta?.title,
                meta?.subTitle,
                meta?.tags?.theme,
                meta?.tags?.product,
                ...(images?.flatMap(img => img.tags ? [img.tags] : []) || []),
            ];
            return parts.filter(Boolean).join('');
        };

        const matchesFilters = (product) => {
            if (filterList.length === 0) return true;
            const tags = product.meta?.tags;
            if (!tags) return false;
            return ['theme', 'product'].some(key =>
                filterList.some(f => tags[key]?.includes(f))
            );
        };

        return productList.filter(product => {
            if (!matchesFilters(product)) return false;
            const productString = buildProductString(product);
            return matchesSearch(productString);
        });
    };

    // ==========================================================================
    //
    // LIFESYCLE - SECTION
    //
    // ==========================================================================

    /**
     * Falls sich das Produkt geändert hat, so müssen die vorhandenen Bilder
     * die dem Produkt zugeordnet wurden in den Zwiswchenspeicher 'imagesJSON' 
     * geladen werden.
     * Zusätzlich werden diese Bilder in den globalen redux-Store geladen werden.
     * damit sie auch im Dialog 'ImageTagModal' zur Verfügung stehen.  
     */
    useEffect(() => {
        if (searchImagesOnly) {
            const newImages = searchImageList(searchString);
            setImages(newImages);
            setGalleryImages(newImages);
        } else {
            setProducts(searchProductList(searchString));
        }

    }, [filterList.length, searchString, searchImagesOnly]);

    // ==========================================================================
    //
    // RENDER - SECTION
    //
    // ==========================================================================

    const getImageUrl = (path, file) => {
        if (appModus === 'Admin') {
            return utils.getURL('product', path, file);

        } else {
            const data = cordova.getImageFromBuffer(path, file);
            return window.URL.createObjectURL(data);
        }
    }

    // ==========================================================================
    // 
    // SUB RENDER
    // 
    // ==========================================================================

    /**
     * Schalter 'searchImagesOnly' anzeigen
     */
    const renderImagesOnlySwitch = () => {

        const style = utils.getStyleHeight(unitHeight, 1.0, 1.0, 0.25);

        const onChange = () => {
            setSearchImagesOnly(!searchImagesOnly);
        }

        return (
            <div className="w-full flex items-start" style={style}>
                <div className="w-10/12 flex mx-auto items-center justify-end">
                    <div className="text-black">nur Bilder</div>
                    <Switch
                        name="nur Bilder"
                        color="primary"
                        checked={searchImagesOnly}
                        onChange={onChange}
                    />
                </div>
            </div>
        )
    }

    /**
     * In diesem Bereich ganz oben werden die ausgewählten Filter aus der 
     * FilterModal-Komponente in einer horizontalen und vertikalen Liste 
     * angezeigt. Hier können einzelne Filter direkt wieder gelöscht werden.
     * 
     * Erweiterungen K-Log
     * Ist der Katalog 'regal-lagersysteme' aktiv, so wird noch der 
     * searchImageOnly-Switch rechts neben den Filtern angezeigt.
     * 
     */
    const renderFilterList = () => {

        const renderFilterItem = (d, i) => {

            const onClick = () => {
                let newFilterList = filterList;
                if (utils.checkFilter(d)) { // löschen
                    newFilterList = filterList.filter(value => value !== d)
                }
                setFilterList(newFilterList);
                cordova.writeLocalJSON(currentContainer, 'filter.json', newFilterList)
            }

            // ###############################################################
            // renderItem()
            // ###############################################################
            const style = { backgroundColor: 'rgb(58, 133, 247)', color: 'white', ...utils.getStyleHeight(unitHeight, .4, .4, .4) };
            const styleText = utils.getStyleHeight(unitHeight, .4, .4, .25);

            return (
                <div key={i} style={style} className="inline-block rounded-lg px-3 mr-2 whitespace-no-wrap" onClick={onClick}>
                    <div style={styleText}>{d}&nbsp;×</div>
                </div>
            )
        }

        // ###############################################################
        // renderFilterList()
        // ###############################################################
        const styleTitle = utils.getStyleHeight(unitHeight, 1.0, 1.0, 0.5);
        const styleItem = utils.getStyleHeight(unitHeight, 1, .4, .4);


        if (currentContainer === 'regal-lagersysteme') {
            return (
                <div className="w-10/12 mx-auto flex" style={styleTitle}>
                    <div className="w-5/6 overflow-auto" style={styleItem} >
                        {filterList.map((d, i) => renderFilterItem(d, i))}
                    </div>
                    <div className="w-1/6 flex items-start" style={styleItem} >
                        {renderImagesOnlySwitch()}
                    </div>
                </div>
            )
        } else {
            return (
                <div className="w-full flex items-center" style={styleTitle}>
                    <div className="w-10/12 flex overflow-x-hidden mx-auto">
                        <div className="w-full overflow-auto" style={styleItem} >
                            {filterList.map((d, i) => renderFilterItem(d, i))}
                        </div>
                    </div>
                </div>
            )
        }
    }

    /**
     * Zwischen der Filter-Liste und der Liste der Suchergebnisse wird das Textfeld
     * zur Eingabe des Suchbegriffs angezeigt. Die Debounce-Mechanik verhindert, dass
     * direkt nach Eingabe eines oder mehrerer Zeichen in kurzer Folge, sofort gesucht wird.
     */
    const renderSearchString = () => {

        // Tastatur schliessen bei [Enter]
        const onKeyDown = (e) => {
            //if (e.keyCode === 13) { e.target.blur() }
        }

        // Wenn Eingabefeld verlassen wurde
        const onBlur = (e) => {
            //e.target.blur(); 
        }

        // Wenn Eingabefeld verändert wurde
        const onChange = (e) => {
            setSearchString(e.target.value);
        }

        // ###############################################################
        // renderSearchString()
        // ###############################################################
        const styleTitle = utils.getStyleHeight(unitHeight, 1.0, 1.0, 0.5);
        const styleInput = { color: 'gray' };
        const style = utils.getStyleHeight(unitHeight, .8, .8, .5);

        return (
            <div className="w-full flex items-center" style={styleTitle}>
                <div className="w-10/12 text-center mx-auto">
                    <div className="w-full flex flex-row border-b-2 border-gray-300 text-gray-800" style={style}>
                        <div className="h-full flex items-center pr-1">
                            <Icon type='search' width={unitHeight * .5} height={unitHeight * .5} />
                        </div>
                        <DebounceInput className="w-full focus:outline-none" minLength={minLength} debounceTimeout={debounceTimeout} autoFocus
                            placeholder="" onChange={onChange} onKeyDown={onKeyDown} onBlur={onBlur} style={styleInput} value={searchString} />
                    </div>
                </div>
            </div>
        )
    }

    /**
     * Diese Funktion zeigt ein einzelnes Product aus dem Suchergebnis 
     */
    const renderProduct = (d, i) => {

        /**
         * Klick eine Zeile (nicht PDF-Image)
         * Wenn der Benutzer eine Zeile der Liste anklickt, dann wird
         * die Detailseite des Produktes angezeigt.
         */
        const onClick = () => {
            setSearchIndex(i, d.path);
            setCurrentProduct(d);

            const dialog = utils.getDialogByPath(d.path);
            const changePortfilio = utils.getPortfolio(dialog.portfolio);
            setCurrentPortfolio(changePortfilio);

            setGallerySearch(searchString !== '');
        }

        /**
         * Klick auf PortfolioIcon [ LG ]
         * Wenn der Benutzer auf das PortfolioIcon klickt, dann wird
         * Normalerweise in die Produktgruppen-Ansicht des Portfolios
         * gesprungen, dies wird hier jedoch explizit nicht gemacht.
         */
        const onClickIconPortfolio = () => {
            // nicht zur Produktgruppe springen
            // setSearchIndex(i, dialog.portfolio);
        }

        /**
         * Hier wird das Freisteller-Bild des Produktes angezeigt.
         */
        const renderIconFree = (d) => {
            const fileObj = utils.getFileInNode(d.children, 'free');
            if (!fileObj) return null;

            return (<ImageLoader path={fileObj.path} name={fileObj.name} height={'100%'} cover={'75%'} />)
        }

        // ###############################################################
        // renderItem()
        // ###############################################################
        const dialog = utils.getDialogByPath(d.path);
        const portfolio = utils.getPortfolio(dialog.portfolio);

        const style = {
            color: 'black',
            marginBottom: '4px',
            backgroundColor: (i % 2) ? 'rgb( 241, 241, 241)' : 'transparent',
            ...utils.getStyleHeight(itemHeight, 1.0, 1.0, 0.5),
        }

        return (
            <div key={`item${i}`} className="w-full flex" style={style}>
                <div className="w-1/4 flex items-center justify-center" onClick={onClick}>
                    {renderIconFree(d)}
                </div>
                <div className="w-1/4 flex items-center justify-center" onClick={onClick}>
                    <ProductDetails node={d} height={itemHeight * 0.2} version='left' />
                </div>
                <div className="w-1/4 flex items-center justify-center" onClick={onClick}>
                    {IconPortfolio(portfolio, itemHeight, onClickIconPortfolio)}
                </div>
                <div className="w-1/4 flex items-center justify-center">
                    <PDFFrame node={d} type='product' listView={true} />
                </div>
            </div>
        )
    }

    /**
     * Einzelnes Bild anzeigen 
     */
    const renderImageCard = (d, i) => {

        const onClick = () => {
            setGalleryIndex(i);
            setModalDialog('ImageGalleryModal', '');
        }

        return (
            <div className="pr-2" onClick={onClick}>
                <img src={getImageUrl(d.path, d.file)} alt="" style={{ minWidth: itemHeight, height: itemHeight, objectFit: "cover" }} />
            </div>
        )
    }

    /**
     * Bilder unterhalb eines Produktes anzeigen, falls vorhanden.
     * Im Normalsfall werden alle Produkte angezeigt. Falls auch Bilder zu den Produkten existieren, dann werden diese
     * mit Hilfe der Funktion 'renderProductImages()' angzeigt.
     */

    const renderProductImages = (d, i) => {


        const searchStringUpper = searchString.replace(/\s/g, '').toUpperCase();

        let productImages = [];
        if (d.images?.length) {
            for (const image of d.images) {
                const hasMatch = image.tags?.some(tag =>
                    tag.toUpperCase().includes(searchStringUpper)
                );

                if (hasMatch) { productImages.push({ path: d.path, file: image.file, tags: image.tags }) }
            }
        }

        const onClick = (index) => {
            setGalleryImages(productImages);
            setGalleryIndex(index);
            setModalDialog('ImageGalleryModal', '');
        }

        return (
            <div className="w-full flex overflow-y-auto py-4">
                {productImages.map((d, i) => {
                    return (
                        <div className="pr-2" onClick={() => onClick(i)}>
                            <img src={getImageUrl(d.path, d.file)} alt="" style={{ minWidth: itemHeight * .5, height: itemHeight * .5, objectFit: "cover" }} />
                        </div>
                    )
                })}
            </div>
        )
    }

    /**
     * Alle Produkte anzeigen
     * Im Normalsfall werden alle Produkte angezeigt. Falls auch Bilder zu den Produkten existieren, dann werden diese
     * mit Hilfe der Funktion 'renderProductImages()' angzeigt.
     */
    const renderAllProducts = (d, i) => {
        //console.log('products:', products);

        return (
            <div className="w-full flex overflow-y-auto">
                <div className="w-10/12 flex flex-col overflow-x-hidden mx-auto">
                    {products.map((d, i) => {
                        return (
                            <div>
                                {renderProduct(d, i)}
                                {renderProductImages(d, i)}
                            </div>
                        )
                    }
                    )}
                </div>
            </div>
        )
    }

    /**
     * Alle Bilder anzeigen
     * Wenn der Schalter 'searchImagesOnly' gesetzt ist, dann werden keine Produkte sondern ausschliesslich die Bilder aller 
     * Produkte angezeigt. 
     */
    const renderAllImages = (d, i) => {
        //console.log('images:', images);

        return (
            <div className="w-full flex overflow-y-auto">
                <div className="w-10/12 grid grid-cols-5 gap-8 overflow-x-hidden mx-auto">
                    {images.map((d, i) => renderImageCard(d, i))}
                </div>
            </div>
        )
    }


    // ==========================================================================
    // 
    // MAIN RENDER
    // 
    // ==========================================================================

    const style = {
        top: headerHeight + 'px',
        height: inputHeight + 'px',
        minHeight: inputHeight + 'px',
        backgroundColor: 'white'
    }

    if (!['search', 'filter'].includes(toolbarType)) return null;

    const styleTitle = utils.getStyleHeight(unitHeight, 1.0, 1.0, 0.5);

    return (
        <div className="SearchController w-full flex z-40" style={style} >
            <div className="w-full flex flex-col text-white">

                {renderFilterList()}

                {renderSearchString()}

                {searchImagesOnly ? renderAllImages() : renderAllProducts()}

            </div>
        </div>
    )
}

export default SearchController;